// Pure logic behind the MM account detail page (SM-564 / SM-565).
//
// Lives in lib/ with relative extensioned imports for the reason
// runHistory.ts gives: the test runner has no alias table, so a rule
// that only lives inside a component is a rule no test can execute.
// The tab vocabulary, the URL-facing guards, the currency-filter rules
// and the row-level helpers are here; the components render them.

import { CURRENCY_CODES } from "./currency.ts";
import { debitCurrency, payoutCurrency } from "./tradingMode.ts";
import {
  TRADING_MODES,
  type MMAccountBalance,
  type MMAccountRun,
  type MMMakerAccount,
  type MMMovementDirection,
  type MMRunStateMetadata,
} from "./types.ts";

/** The three primary tabs. Order is the order they render. */
export const ACCOUNT_TABS = ["trading", "balances", "runs"] as const;
export type AccountTab = (typeof ACCOUNT_TABS)[number];

export function isAccountTab(value: unknown): value is AccountTab {
  return ACCOUNT_TABS.includes(value as AccountTab);
}

/**
 * Trading sub-sections. "orders" and "positions" are live; "history"
 * (order lifecycle) and "trades" (fills) are histories. Kept separate
 * from the run-state vocabulary because the account view has a section
 * the run view does not (order history).
 */
export const TRADING_SECTIONS = ["orders", "positions", "history", "trades"] as const;
export type TradingSection = (typeof TRADING_SECTIONS)[number];

export function isTradingSection(value: unknown): value is TradingSection {
  return TRADING_SECTIONS.includes(value as TradingSection);
}

/** Runs sub-filter: what the tab's status group means on the wire. */
export const RUN_FILTERS = ["all", "active", "history"] as const;
export type RunFilter = (typeof RUN_FILTERS)[number];

export function isRunFilter(value: unknown): value is RunFilter {
  return RUN_FILTERS.includes(value as RunFilter);
}

/**
 * The `statuses` parameter for a run filter. "all" sends NOTHING (an
 * empty `statuses=` would be a filter matching no run); "active" is the
 * lifecycle's active set including PAUSED; "history" is the terminal
 * pair.
 */
export function runFilterStatuses(filter: RunFilter): string | undefined {
  switch (filter) {
    case "active":
      return "PENDING,RUNNING,PAUSING,PAUSED,RESUMING,STOPPING";
    case "history":
      return "STOPPED,FAILED";
    default:
      return undefined;
  }
}

/**
 * The page-level currency filter: "all" or one canonical code the
 * SERVER listed for this account. A code the server did not list falls
 * back to "all" rather than sending a filter the server would 400 —
 * and rather than a hard-coded RED/GREEN/GOLD guard, so a fourth
 * currency needs no console change.
 */
export const ALL_CURRENCIES = "all";
export type CurrencyFilter = typeof ALL_CURRENCIES | string;

export function normalizeCurrencyFilter(value: unknown, known: readonly string[] | undefined): CurrencyFilter {
  if (typeof value !== "string" || value === "" || value === ALL_CURRENCIES) return ALL_CURRENCIES;
  const upper = value.toUpperCase();
  // Before the server has answered, trust the URL's shape (an uppercase
  // code) so a deep link is not rewritten to "all" during the first
  // render and then flipped back.
  if (known === undefined) return /^[A-Z]{2,12}$/.test(upper) ? upper : ALL_CURRENCIES;
  return known.includes(upper) ? upper : ALL_CURRENCIES;
}

/** The wire value for a currency filter: the code, or absent for "all". */
export function currencyParam(filter: CurrencyFilter): string | undefined {
  return filter === ALL_CURRENCIES ? undefined : filter;
}

/**
 * Registry order for the currency strip: the console's fixed coin
 * order first for codes it knows, then anything else the server named,
 * alphabetically. The set comes from the server; only the ORDER is
 * local.
 */
export function orderCurrencies(codes: readonly string[]): string[] {
  const rank = new Map(CURRENCY_CODES.map((c, i) => [c as string, i]));
  return [...new Set(codes)].sort((a, b) => {
    const ra = rank.get(a);
    const rb = rank.get(b);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return a.localeCompare(b);
  });
}

/**
 * The currency the balance-history journal reads when the page filter
 * is "all": the ledger pages one wallet, so the tab needs a concrete
 * choice. The first currency with a readable execution balance, else
 * the first configured one.
 */
export function defaultHistoryCurrency(
  filter: CurrencyFilter,
  balances: readonly MMAccountBalance[] | undefined,
  currencies: readonly string[],
): string | undefined {
  if (filter !== ALL_CURRENCIES) return filter;
  const ordered = orderCurrencies(currencies);
  const funded = ordered.find((c) =>
    balances?.some((b) => b.currency === c && b.execution_available_minor !== undefined),
  );
  return funded ?? ordered[0];
}

/**
 * Balance rows in registry order and narrowed to the filter. Never
 * merges two currencies into one row.
 */
export function visibleBalances(
  balances: readonly MMAccountBalance[],
  filter: CurrencyFilter,
): MMAccountBalance[] {
  const order = orderCurrencies(balances.map((b) => b.currency));
  return order
    .filter((c) => filter === ALL_CURRENCIES || c === filter)
    .map((c) => balances.find((b) => b.currency === c))
    .filter((b): b is MMAccountBalance => b !== undefined);
}

/**
 * Whether a balance row can back a money-moving action in its
 * currency: the reserve must be linked AND provisioned in that
 * currency, and the figure the action spends must be READABLE. An
 * unknown balance blocks — "we could not read it" is never a reason
 * to move money against it.
 */
export function canFundExecution(row: MMAccountBalance, account: Pick<MMMakerAccount, "funding_link_status">): boolean {
  return (
    account.funding_link_status === "LINKED" &&
    !!row.funding_account_code &&
    row.reserve_provisioned !== false &&
    row.funding_reserve_available_minor !== undefined &&
    row.funding_reserve_available_minor > 0
  );
}

export function canReturnToReserve(row: MMAccountBalance, account: Pick<MMMakerAccount, "funding_link_status">): boolean {
  return (
    account.funding_link_status === "LINKED" &&
    !!row.funding_account_code &&
    row.reserve_provisioned !== false &&
    row.execution_available_minor !== undefined &&
    row.execution_available_minor > 0
  );
}

/** A balance movement's tone: credits read positive, debits negative, holds neutral. */
export function movementTone(direction: MMMovementDirection | string): "yes" | "no" | "neutral" {
  switch (direction) {
    case "CREDIT":
      return "yes";
    case "DEBIT":
      return "no";
    default:
      return "neutral";
  }
}

/**
 * Whether a run row survives the page currency filter: a run is shown
 * when either of its two legs is the selected currency, so a GREEN
 * filter shows GAME runs (which debit RED and pay out GREEN). Records
 * with a mode context are never dropped merely for not being
 * single-currency — the row shows both legs.
 */
export function runMatchesCurrency(run: Pick<MMAccountRun, "debit_currency" | "payout_currency">, filter: CurrencyFilter): boolean {
  if (filter === ALL_CURRENCIES) return true;
  return run.debit_currency === filter || run.payout_currency === filter;
}

/** Sections that poll: live state only. Histories are re-read on entry and after mutations. */
export function sectionPolls(section: TradingSection): boolean {
  return section === "orders" || section === "positions";
}

/** The freshness header a LIVE section shows: complete reads say "live", degraded ones say what failed. */
export function sectionIsDegraded(state: MMRunStateMetadata | undefined): boolean {
  return !!state && state.completeness !== "COMPLETE";
}

/**
 * The trading mode whose wallet pair contains the currency — the same
 * fold the server applies to a section's `?currency=` (GOLD → CASH,
 * RED or GREEN → GAME), reused here for the Runs tab whose endpoint
 * takes a mode. Built from the two existing leg helpers so the console
 * has one statement of the policy.
 */
export function tradingModeForCurrency(currency: string): string | undefined {
  return TRADING_MODES.find((mode) => debitCurrency(mode) === currency || payoutCurrency(mode) === currency);
}

/**
 * placeholderData for a paged section: keep the previous page's rows
 * visible ONLY while the account, currency and mode are unchanged. A
 * change of account or currency must never flash the old selection's
 * rows — React Query would otherwise happily show them under the new
 * key until the fetch lands.
 *
 * `scopeLength` is how many leading key elements identify the scope
 * (everything before limit/offset).
 */
export function keepWithinScope<T>(currentKey: readonly unknown[], scopeLength: number) {
  return (previous: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined): T | undefined => {
    if (!previous || !previousQuery) return undefined;
    return sameScope(previousQuery.queryKey, currentKey, scopeLength) ? previous : undefined;
  };
}

export function sameScope(a: readonly unknown[], b: readonly unknown[], scopeLength: number): boolean {
  for (let i = 0; i < scopeLength; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
